import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { DropdownOption } from "../../../components";
import { MultiSelectDropdownOptions } from "../../../components/MultiSelectDropdown";
import { LocationService } from "../../../services";
import { getUserInfo, RootState } from "../../../app";
import { setLocationPageLocation, setLocationPageProviders } from "../../../app/store/calendarPreferencesSlice";
import { LocationProvidersModel } from "../../../data/entities";

export function useCalendarLocationSelection() {
  const dispatch = useDispatch();
  const locationService = useMemo(() => new LocationService(), []);
  const currentUserId = useSelector(getUserInfo())?.id;
  
  const cachedPreferences = useSelector((state: RootState) => 
    currentUserId ? state.calendarPreferences.locationPreferences[currentUserId.toString()] : undefined
  );

  const [accessibleLocations, setAccessibleLocations] = useState<LocationProvidersModel[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const previousLocationIdRef = useRef<string>("");
  const initializedUserIdRef = useRef<string>("");
  
  // Dropdown state
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [selectedProviders, setSelectedProviders] = useState<MultiSelectDropdownOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<DropdownOption[]>([]);
  const [providerOptions, setProviderOptions] = useState<DropdownOption[]>([]);

  const currentLocationDetails = useMemo(() => {
    if (!selectedLocationId) return [];
    const location = accessibleLocations.find(l => l.locationId.toString() === selectedLocationId);
    return location?.details ?? [];
  }, [accessibleLocations, selectedLocationId]);

  const filteredDetails = useMemo(() => {
    if (selectedProviders.length === 0) {
      return [];
    }
    
    if (selectedProviders.some(p => p.value === "")) {
      return currentLocationDetails;
    }
    
    const selectedProviderIds = selectedProviders
      .filter(p => p.value !== "")
      .map(p => p.value);
    
    const filtered = currentLocationDetails.filter(detail => 
      selectedProviderIds.includes(detail.providerId.toString())
    );    
    return filtered;
  }, [currentLocationDetails, selectedProviders]);

  const dropdownResults = useMemo(() => {
    return filteredDetails.map(detail => ({
      locationId: detail.locationId,
      locationName: detail.locationName,
      providerId: detail.providerId,
      providerName: detail.providerName,
    }));
  }, [filteredDetails]);

  const lastFetchRef = useRef<{ locationId: string; providerIds: string | null } | null>(null);

  useEffect(() => {
    if (!currentUserId) {
      setIsInitialized(true);
      return;
    }

    if (initializedUserIdRef.current === currentUserId.toString()) {
      return;
    }
    initializedUserIdRef.current = currentUserId.toString();

    const initializeData = async () => {
      try {
        const allLocations = await locationService.getLocationsByRoleAccess(currentUserId);

        if (!allLocations || allLocations.length === 0) {
          setIsInitialized(true);
          return;
        }

        // Inactive providers are hidden from the calendar and its dropdowns
        const locations = allLocations.map(location => ({
          ...location,
          details: location.details?.filter(d => d.isActive) ?? [],
        }));

        setAccessibleLocations(locations);

        const locOpts = locations
          .map(location => ({
            text: location.locationName,
            value: location.locationId.toString(),
            code: location.businessCode ?? "",
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
        
        setLocationOptions(locOpts);

        let locationIdToSelect = "";
        
        // Restore cached location
        if (cachedPreferences?.selectedLocationId) {
          const locationExists = locations.some(
            l => l.locationId.toString() === cachedPreferences.selectedLocationId
          );
          if (locationExists) {
            locationIdToSelect = cachedPreferences.selectedLocationId;
          }
        }
        
        // If no cached location and only one option, auto-select it
        if (!locationIdToSelect && locOpts.length === 1) {
          locationIdToSelect = locOpts[0].value;
        }
        
        setSelectedLocationId(locationIdToSelect);
        previousLocationIdRef.current = locationIdToSelect;

        // Restore providers for this specific location; when there is no valid
        // cached selection (e.g. the location was picked on the home agenda,
        // which has no provider filter), default to "All Providers" so the
        // calendar doesn't load with nothing selected
        if (locationIdToSelect) {
          const cachedProviderIds =
            cachedPreferences?.providerSelectionsByLocation?.[locationIdToSelect] ?? [];

          const selectedLocation = locations.find(
            l => l.locationId.toString() === locationIdToSelect
          );

          let restoredProviders: MultiSelectDropdownOptions[] = [];

          if (selectedLocation?.details && cachedProviderIds.length > 0) {
            const availableProviderIds = selectedLocation.details.map(d => d.providerId.toString());
            const validCachedProviders = cachedProviderIds.filter(id =>
              id === "" || availableProviderIds.includes(id)
            );

            restoredProviders = validCachedProviders.map(id => {
              if (id === "") {
                return { value: "", text: "All Providers" };
              }
              const detail = selectedLocation.details.find(d => d.providerId.toString() === id);
              return { value: id, text: detail?.providerName || id };
            });
          }

          if (restoredProviders.length === 0) {
            restoredProviders = [{ value: "", text: "All Providers" }];
          }

          setSelectedProviders(restoredProviders);
        }

        setIsInitialized(true);
      } catch (error) {
        setIsInitialized(true);
      }
    };

    initializeData();
  }, [currentUserId, locationService, cachedPreferences]);

  // Update provider options when selected location changes
  useEffect(() => {
    if (!isInitialized) return;

    const baseOptions = [
      { key: "", value: "", text: "All Providers" }
    ];

    if (!selectedLocationId) {
      setProviderOptions([]);
      setSelectedProviders([]);
      previousLocationIdRef.current = "";
      return;
    }

    const selectedLocation = accessibleLocations.find(
      l => l.locationId.toString() === selectedLocationId
    );

    if (!selectedLocation?.details?.length) {
      setProviderOptions(baseOptions);
      setSelectedProviders([]);
      return;
    }

    const locationProviders = selectedLocation.details
      .map(detail => ({
        key: detail.providerId.toString(),
        value: detail.providerId.toString(),
        text: detail.providerName,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    const provOpts = [...baseOptions, ...locationProviders];
    setProviderOptions(provOpts);
    
    // Check if location changed
    const locationChanged = previousLocationIdRef.current !== selectedLocationId;
    
    if (locationChanged) {
      const cachedForThisLocation = cachedPreferences?.providerSelectionsByLocation?.[selectedLocationId];
      
      if (cachedForThisLocation && cachedForThisLocation.length > 0) {
        const availableProviderIds = selectedLocation.details.map(d => d.providerId.toString());
        const validCachedProviders = cachedForThisLocation.filter(id => 
          id === "" || availableProviderIds.includes(id)
        );
        
        if (validCachedProviders.length > 0) {
          const restoredProviders = validCachedProviders.map(id => {
            if (id === "") {
              return { value: "", text: "All Providers" };
            }
            const detail = selectedLocation.details.find(d => d.providerId.toString() === id);
            return { value: id, text: detail?.providerName || id };
          });
          
          setSelectedProviders(restoredProviders);
        } else {
          // Invalid cache, default to "All Providers"
          setSelectedProviders([baseOptions[0]]);
          
          // Cache this default selection
          if (currentUserId) {
            dispatch(
              setLocationPageProviders({
                userId: currentUserId.toString(),
                locationId: selectedLocationId,
                selectedProviderIds: [""],
              })
            );
          }
        }
      } else {
        // No cache for this location default to "All Providers"
        setSelectedProviders([baseOptions[0]]);
        
        // Cache this default selection
        if (currentUserId) {
          dispatch(
            setLocationPageProviders({
              userId: currentUserId.toString(),
              locationId: selectedLocationId,
              selectedProviderIds: [""],
            })
          );
        }
      }
    }
    
    previousLocationIdRef.current = selectedLocationId;
  }, [selectedLocationId, isInitialized, accessibleLocations, cachedPreferences, dispatch, currentUserId]);

  const handleSelectLocation = useCallback((e: DropDownListChangeEvent) => {
    const value = e.value;
    if (!value) return;

    const newLocationId = value.value;
    setSelectedLocationId(newLocationId);
    
    if (currentUserId) {
      dispatch(
        setLocationPageLocation({
          userId: currentUserId.toString(),
          selectedLocationId: newLocationId,
        })
      );
    }
  }, [dispatch, currentUserId]);

  const handleSelectProviders = useCallback((selections: MultiSelectDropdownOptions[]) => {
    setSelectedProviders(selections);
    
    // Cache provider selection for current location
    if (currentUserId && selectedLocationId) {
      const providerIds = selections.map(s => s.value);
      dispatch(
        setLocationPageProviders({
          userId: currentUserId.toString(),
          locationId: selectedLocationId,
          selectedProviderIds: providerIds,
        })
      );
    }
  }, [dispatch, currentUserId, selectedLocationId]);

  const getProviderIdsString = useCallback(() => {
    if (selectedProviders.length === 0) {
      return null;
    }
    
    if (selectedProviders.some(p => p.value === "")) {
      return "";
    }
    
    const filtered = selectedProviders.filter(p => p.value !== "");
    return filtered.map(p => p.value).join(",");
  }, [selectedProviders]);

  const showLocationDefaultItem = !locationOptions.some(loc => loc.value === selectedLocationId);

  return {
    selectedLocationId,
    selectedProviders,
    locationOptions,
    providerOptions,
    isInitialized,
    accessibleLocations,
    currentLocationDetails,
    filteredDetails,
    dropdownResults,
    lastFetchRef,
    showLocationDefaultItem,
    handleSelectLocation,
    handleSelectProviders,
    getProviderIdsString,
  };
}